import {
    getSquareOnlineStoreEcommerceBrief,
    getSquareOnlineStoreEcommerceBriefVariables,
} from '__generated__/getSquareOnlineStoreEcommerceBrief';
import { SquareOnlineStoreEcommerceBriefFields } from '__generated__/SquareOnlineStoreEcommerceBriefFields';
import DesignerCardContext from 'projects/components/DesignerCardContext';
import PartnerContext, { Partner } from 'projects/PartnerContext';
import Brief from 'projects/routes/Brief';
import BriefQuery from 'projects/routes/BriefQuery';
import InviteDesigners from 'projects/routes/InviteDesignersPage';
import { BriefStep, StepState } from 'projects/util/step';
import React from 'react';
import { Route, Switch, useRouteMatch } from 'react-router';
import DetailsPage from './DetailsPage';
import queries from './SquareOnlineStoreEcommerce.graphql';
import SummaryPage from './SummaryPage';

interface Props {
    briefId: string;
    step: string;
}

const steps: BriefStep<SquareOnlineStoreEcommerceBriefFields>[] = [
    {
        step: 1,
        title: __('Tell us about your project'),
        fields: ['title', 'existingWebsite', 'description', 'referenceFiles', 'other'],
    },
    { step: 2, title: __('Review your brief') },
    {
        step: 3,
        title: __('Choose a designer'),
        isAuthRequired: true,
        isDesignerStep: true,
        nextActionLabel: __('Confirm designer'),
    },
];

export function SquareOnlineStoreEcommerce() {
    const route = useRouteMatch<Props>();

    return (
        <PartnerContext.Provider value={{ partner: Partner.Square }}>
            <BriefQuery<
                getSquareOnlineStoreEcommerceBrief,
                getSquareOnlineStoreEcommerceBriefVariables
            >
                query={queries.getSquareOnlineStoreEcommerceBrief}
                variables={{ id: route!.params.briefId }}
                fieldsTypename="SquareOnlineStoreEcommerceBriefFields"
            >
                {(data, refetch) => {
                    const { viewer } = data;
                    const brief = {
                        ...data.brief,
                        fields: data.brief.fields as SquareOnlineStoreEcommerceBriefFields,
                    };

                    const step = new StepState({
                        steps,
                        brief: data.brief,
                        currentStep: parseInt(route!.params.step, 10),
                    });

                    return (
                        <Brief
                            refetch={refetch}
                            schema="squareOnlineStoreEcommerce"
                            brief={brief}
                            viewer={viewer}
                            stepState={step}
                            disableContacts={true}
                            totalAllowableDesigners={1}
                            briefPackage={brief.package}
                        >
                            {form => (
                                <Switch>
                                    <Route
                                        path={`/briefs/square-online-store-ecommerce/:briefId/1`}
                                        render={() => <DetailsPage form={form} brief={brief} />}
                                    />
                                    <Route
                                        path={`/briefs/square-online-store-ecommerce/:briefId/2`}
                                        render={() => <SummaryPage brief={brief} />}
                                    />
                                    <Route
                                        path={`/briefs/square-online-store-ecommerce/:briefId/3`}
                                        render={() => (
                                            <DesignerCardContext.Provider
                                                value={{
                                                    selectDesignerLabels: {
                                                        selected: __('Selected'),
                                                        notSelected: __('Select'),
                                                    },
                                                }}
                                            >
                                                <InviteDesigners
                                                    brief={brief}
                                                    viewer={viewer}
                                                    description={__(
                                                        'We’ve matched you with these awesome designers. Select your favourite one to work with.'
                                                    )}
                                                />
                                            </DesignerCardContext.Provider>
                                        )}
                                    />
                                </Switch>
                            )}
                        </Brief>
                    );
                }}
            </BriefQuery>
        </PartnerContext.Provider>
    );
}
